/** Matches server {@link com.fleet.doctor.access.PermissionModuleTaxonomy#ORDERED_MODULES} (sidebar-level only). */
export const PERMISSION_MODULE_ORDER = [
  'Admin',
  'Dashboard',
  'Operations',
  'Crewing',
  'Technical',
  'QHSE',
  'Procurement',
  'Inventory',
  'Vessels',
  'Reports',
  'Other',
] as const

export type PermissionModuleKey = (typeof PERMISSION_MODULE_ORDER)[number] | string

export type PermissionCatalogEntry = {
  permissionId: number
  permissionCode: string
  displayName?: string | null
  moduleCategory?: string | null
  description?: string | null
  sortOrder?: number | null
  /** From permission_menu_map (backend): menu column in matrix */
  menuLabel?: string | null
  subMenuLabel?: string | null
  /** Action type (e.g. VIEW, ADD, EDIT, DELETE, DOWNLOAD) */
  action?: string | null
}

const normalizeCode = (s: string): string => s.trim().toUpperCase()

const has = (u: string, ...parts: string[]) => parts.some((p) => u.includes(p))

/**
 * Client fallback when API omits moduleCategory — mirrors server
 * {@link com.fleet.doctor.access.PermissionModuleTaxonomy#categorize} (top-level modules only).
 */
export function categorizePermissionFallback(permissionCode: string): string {
  const u = normalizeCode(permissionCode)
  if (!u) return 'Other'

  if (has(u, 'DASHBOARD', 'HOME_WIDGET')) {
    return 'Dashboard'
  }
  if (has(u, 'QHSE', 'WORK_PERMIT', 'RISK_ASSESSMENT', 'SAFETY_INSPECTION', 'INCIDENT', 'NEAR_MISS')) {
    return 'QHSE'
  }
  if (
    has(
      u,
      'REQUISITION',
      'QUOTATION',
      'PURCHASE_ORDER',
      'PO_',
      'GRN_',
      'INVOICE_',
      'GOODS_RECEIPT',
      'VENDOR_INVOICE',
      'RFQ',
      'PROCUREMENT',
      'DELIVERY_',
      'VENDOR_LIBRARY',
      'BUNKER_TEMPLATE'
    )
  ) {
    return 'Procurement'
  }
  if (has(u, 'INVENTORY_ITEM', 'INVENTORY_', 'STOCK_', 'WAREHOUSE')) {
    return 'Inventory'
  }
  if (
    has(
      u,
      'TECHNICAL',
      'PMS_',
      'MAINTENANCE',
      'DRY_DOCK',
      'SERVICE_ITEM',
      'RISK_LIBRARY',
      'MACHINERY',
      'EQUIPMENT',
      'READ_PART',
      'CREATE_PART',
      'UPDATE_PART',
      'DELETE_PART',
      'IMPORT_PART',
      'EXPORT_PART',
      'READ_SUBCOMPONENT',
      'CREATE_SUBCOMPONENT',
      'UPDATE_SUBCOMPONENT',
      'DELETE_SUBCOMPONENT',
      'IMPORT_SUBCOMPONENT',
      'EXPORT_SUBCOMPONENT',
      'READ_MACHINERY',
      'CREATE_MACHINERY',
      'UPDATE_MACHINERY',
      'DELETE_MACHINERY',
      'IMPORT_MACHINERY',
      'EXPORT_MACHINERY'
    )
  ) {
    return 'Technical'
  }
  if (
    has(
      u,
      'VOYAGE_REPORT',
      'POSITION_REPORT',
      'CARGO_',
      'CARGO',
      'BUNKER_',
      'VOYAGE',
      'VESSEL',
      'FLEET_REGISTRY',
      'FLEET_',
      'OPERATION'
    ) &&
    !u.includes('QHSE')
  ) {
    return 'Operations'
  }
  if (u.includes('DEFECT') && !u.includes('QHSE')) {
    return 'Operations'
  }
  if (has(u, 'CREW', 'MANNING', 'SEAFARER', 'REST_HOUR', 'RANK', 'SIGN_ON', 'SIGN_OFF')) {
    return 'Crewing'
  }
  if (
    has(u, 'REPORT_', 'REPORT_MENU', 'REPORT_FIELD', 'REPORT_TEMPLATE', 'REPORT_ASSIGNMENT') &&
    !has(u, 'VOYAGE', 'POSITION')
  ) {
    return 'Reports'
  }
  if (u.includes('REPORT') && !has(u, 'VOYAGE', 'POSITION')) {
    return 'Reports'
  }
  if (
    has(
      u,
      'READ_USER',
      'CREATE_USER',
      'UPDATE_USER',
      'EDIT_USER_PASSWORD',
      'DELETE_USER',
      'USER_ACCESS',
      'USER_SCOPE',
      'READ_ROLE',
      'CREATE_ROLE',
      'UPDATE_ROLE',
      'DELETE_ROLE',
      'ROLE_PERMISSION',
      'COMPANY_GROUP',
      'COMPANY_ADMIN',
      'READ_COMPANY',
      'CREATE_COMPANY',
      'UPDATE_COMPANY',
      'CGA',
      'VENDOR',
      'SUPPLIER',
      'SUPER_ADMIN',
      'READ_AUDIT',
      'AUTH_CHANGE',
      'MENU_',
      'PERMISSION_REGISTRY',
      'BUNDLE_DEFAULT',
      'ACCESS_BUNDLE',
      'ASSIGNED_BUNDLE'
    )
  ) {
    return 'Admin'
  }
  if (has(u, 'ADMIN')) {
    return 'Admin'
  }
  return 'Other'
}

/** Collapse granular DB / menu labels (matches server {@code collapseGranularDbCategory}). */
export function collapseGranularDbCategory(raw: string): string | null {
  const u = raw.trim().toUpperCase()
  if (!u) return null

  if (has(u, 'QHSE', 'HSE ', 'WORK PERMIT', 'WORK_PERMIT', 'RISK ASSESSMENT', 'RISK_ASSESSMENT')) {
    return 'QHSE'
  }
  if (
    has(
      u,
      'PROCURE',
      'REQUISITION',
      'PURCHASE ORDER',
      'PURCHASE_ORDER',
      'QUOTATION',
      'RFQ',
      'VENDOR INVOICE',
      'VENDOR_INVOICE',
      'GOODS RECEIPT',
      'GOODS_RECEIPT'
    )
  ) {
    return 'Procurement'
  }
  if (has(u, 'INVENT', 'STOCK', 'WAREHOUSE')) {
    return 'Inventory'
  }
  if (has(u, 'TECHNICAL', 'PMS', 'MACHINERY', 'EQUIPMENT', 'MAINTENANCE', 'DRY DOCK', 'DRY_DOCK')) {
    return 'Technical'
  }
  if (has(u, 'CREWINGTEST', 'CREW TEST', 'CREW_TEST')) {
    return 'Crewing'
  }
  if (has(u, 'CREW', 'MANNING', 'SEAFARER', 'REST HOUR', 'REST_HOUR', 'RANKING', 'RANK ')) {
    return 'Crewing'
  }
  if (
    has(
      u,
      'VOYAGE',
      'POSITION REPORT',
      'POSITION_REPORT',
      'CARGO',
      'BUNKER',
      'FLEET',
      'VESSEL',
      'OPERATIONS',
      'OPERATION',
      'VOYAGES'
    ) &&
    !u.includes('QHSE')
  ) {
    return 'Operations'
  }
  if (u.includes('DEFECT') && !u.includes('QHSE')) {
    return 'Operations'
  }
  if (
    has(
      u,
      'REPORT MENU',
      'REPORT_MENU',
      'REPORT_FIELD',
      'REPORT FIELD',
      'REPORT SUBMENU',
      'REPORT_TEMPLATE',
      'REPORT_ASSIGNMENT',
      'REPORT ASSIGNMENT'
    ) &&
    !has(u, 'VOYAGE', 'POSITION')
  ) {
    return 'Reports'
  }
  if (u.includes('REPORT') && !has(u, 'VOYAGE', 'POSITION')) {
    return 'Reports'
  }
  if (
    has(
      u,
      'USER',
      'ROLE',
      'COMPANY',
      'VENDOR',
      'SUPPLIER',
      'ADMIN',
      'AUDIT',
      'MENU',
      'PERMISSION',
      'ACCESS BUNDLE',
      'ACCESS_BUNDLE',
      'SCOPE',
      'SETTINGS',
      'CONFIG'
    )
  ) {
    return 'Admin'
  }
  return null
}

function isCanonicalTopLevel(name: string): boolean {
  const order = PERMISSION_MODULE_ORDER as readonly string[]
  const t = name.trim()
  return order.some((o) => o.toLowerCase() === t.toLowerCase())
}

function canonicalTopLevelCase(name: string): string {
  const order = PERMISSION_MODULE_ORDER as readonly string[]
  const t = name.trim()
  const hit = order.find((o) => o.toLowerCase() === t.toLowerCase())
  return hit ?? t
}

export function humanizePermissionCode(permissionCode: string): string {
  if (!permissionCode?.trim()) return ''
  return permissionCode
    .split('_')
    .filter(Boolean)
    .map((p) => p.charAt(0) + p.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Server returns sidebar-level moduleCategory when possible; this also collapses granular labels and
 * falls back to code rules when the API still sends menu/feature names.
 */
export function resolveCatalogEntry(entry: PermissionCatalogEntry): PermissionCatalogEntry {
  const code = normalizeCode(entry.permissionCode)
  const dn =
    (entry.displayName && entry.displayName.trim()) ||
    humanizePermissionCode(code)

  let mod: string
  if (entry.moduleCategory?.trim()) {
    const raw = entry.moduleCategory.trim()
    const collapsed = collapseGranularDbCategory(raw)
    if (collapsed) {
      mod = collapsed
    } else if (isCanonicalTopLevel(raw)) {
      mod = canonicalTopLevelCase(raw)
    } else {
      mod = categorizePermissionFallback(code)
    }
  } else {
    mod = categorizePermissionFallback(code)
  }

  return {
    ...entry,
    permissionCode: code,
    displayName: dn,
    moduleCategory: mod,
    menuLabel: entry.menuLabel?.trim() || null,
    subMenuLabel: entry.subMenuLabel?.trim() || null,
  }
}

export function moduleOrderIndex(moduleLabel: string): number {
  const order = PERMISSION_MODULE_ORDER as readonly string[]
  const i = order.indexOf(moduleLabel)
  return i < 0 ? 999 : i
}

export function sortEntriesInModule<T extends {permissionCode: string; displayName?: string | null}>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => {
    const da = (a.displayName || a.permissionCode).toLowerCase()
    const db = (b.displayName || b.permissionCode).toLowerCase()
    if (da !== db) return da.localeCompare(db)
    return a.permissionCode.localeCompare(b.permissionCode)
  })
}

export type GroupedPermissionSection<T> = {
  moduleKey: string
  items: T[]
}

/**
 * Group catalog entries by module; filter by search across code, displayName, description, module;
 * optionally filter items with predicate.
 */
export function groupPermissionCatalog<T extends PermissionCatalogEntry>(
  catalog: T[],
  options: {
    searchQuery: string
    filter?: (e: T) => boolean
    mapRow?: (e: T) => T
    /** If the default code/displayName/description/module search misses, use this (query is uppercased). */
    extraSearch?: (e: T, queryUpper: string) => boolean
  }
): GroupedPermissionSection<T>[] {
  const q = options.searchQuery.trim().toUpperCase()
  let list = catalog.map((e) => (options.mapRow ? options.mapRow(e) : e))
  list = list.map((e) => resolveCatalogEntry(e) as T)
  if (options.filter) list = list.filter(options.filter)
  if (q) {
    list = list.filter((e) => {
      const resolved = resolveCatalogEntry(e)
      const base =
        resolved.permissionCode.toUpperCase().includes(q) ||
        (resolved.displayName || '').toUpperCase().includes(q) ||
        (resolved.description || '').toUpperCase().includes(q) ||
        (resolved.moduleCategory || '').toUpperCase().includes(q)
      if (base) return true
      return options.extraSearch?.(e, q) ?? false
    })
  }
  const byModule = new Map<string, T[]>()
  for (const e of list) {
    const r = resolveCatalogEntry(e)
    const m = r.moduleCategory || 'Other'
    if (!byModule.has(m)) byModule.set(m, [])
    byModule.get(m)!.push(e)
  }
  for (const key of Array.from(byModule.keys())) {
    const items = byModule.get(key)!
    byModule.set(key, sortEntriesInModule(items as {permissionCode: string; displayName?: string | null}[]) as T[])
  }
  const keys = Array.from(byModule.keys())
  keys.sort((a, b) => moduleOrderIndex(a) - moduleOrderIndex(b))
  return keys.map((moduleKey) => ({moduleKey, items: byModule.get(moduleKey)!}))
}
